import { Cartographic, sampleTerrainMostDetailed } from "cesium";

/**
 * Lift the parts of a track that ended up below the terrain model.
 *
 * IGC files that start with the walk-up (hike-and-fly, or just a vario left
 * running on the way to launch) draw straight through the mountain: the fixes
 * are on the ground, and GPS altitude on the ground is wrong by more than the
 * ground is thick. Consumer GPS vertical error is 1.5–3× its horizontal error
 * and gets worse under trees and in valleys — 20–40 m is normal — while the
 * terrain itself is a ~10 m-post model that smooths gullies upwards. Both
 * errors point the same way, and `globe.depthTestAgainstTerrain = true`
 * (ViewerComponent) then hides whatever ends up inside the hill.
 *
 * So we sample the terrain under the track and raise only the fixes that are
 * below it, leaving everything else exactly as recorded. This is deliberately
 * NOT a single global offset: a walk in the forest can need 40 m while the
 * flight above needs none, and shifting the whole flight would then park the
 * landing 40 m in the air.
 *
 * The lifted value goes into `dAltitude`, a *display* altitude. `altitude`
 * stays the recorded one, so the label, the info box, the chart and the climb
 * colours are untouched — only the drawn geometry moves (`czmlHelper` path +
 * marker, `varioTrack` polylines, and with them the follow/cinema camera,
 * which reads the entity position).
 *
 * ## Why probes are spread by TIME, not by distance
 *
 * The obvious decimation — a probe every N metres along the track — spends the
 * budget on the glides and starves the walk: on a 338 km XC (`__cinema-test`)
 * an even 1200 probes land 282 m apart, which leaves a 2.5 km walk-up with
 * about seven of them. Every N-th *fix* instead puts probes where the fixes
 * are, i.e. where the pilot is slow, i.e. exactly where the track is on the
 * ground. Measured on that flight, every-58th-fix probes sit 2 m apart at the
 * 10th percentile and 40 m at the median, stretching to 612 m only on glides,
 * where nothing is near the ground anyway.
 *
 * Probe count is nearly free: `sampleTerrainMostDetailed` groups positions by
 * tile, so the download is set by how much ground the track covers, not by how
 * often we ask. Unique z15 tiles for the three test files: 265 (338 km XC),
 * 114 (156 km) and 28 (`__ebenalp`, 44 km) — a thermalling track keeps coming
 * back to the same tiles.
 *
 * ## What the margin does and does not cover
 *
 * `MARGIN_M` covers the DEM's own error and the ramp between probes (measured
 * on the walk-up in `__cinema-test`, the required lift drifts ~15 m over 35
 * fixes, so a few metres of ramp error), and at any normal viewing distance it
 * still reads as "on the ground".
 *
 * It does NOT cover terrain LOD. Sampling returns the most detailed surface,
 * but the *rendered* surface is whatever level the camera currently asks for,
 * and a coarse tile fills valleys in: measured over that same walk, a level-13
 * tile reads 2045 m where the detailed surface is 1594 m. Nothing that keeps
 * the track on the ground close up can also keep it out of a hill that is
 * drawn 450 m too high — a track zoomed far out can still disappear, and that
 * is an LOD problem, not this one. Don't grow the margin trying to fix it.
 */

// Clearance kept above the sampled surface, metres. See note above.
const MARGIN_M = 10;
// Never lift more than this — a backstop against a file whose altitudes are in
// some other datum entirely (pressure altitude in the GPS field, say), where
// silently hoisting the track would hide the real problem. It has to clear
// ordinary GPS drift: a logger left running overnight in `__cinema-test`
// wandered 136 m below the ground it was sitting on.
const MAX_LIFT_M = 250;
// Probe every N-th fix, N sized to keep the count near this. At 1 Hz a 4 h
// flight lands ~1 probe/minute in the air, tens of metres apart where slow.
const MAX_SAMPLES = 1500;
// …but never denser than this, so short files don't probe every single fix.
const MIN_FIX_STEP = 5;
// A `?file=` load can beat `setTerrain()` — wait rather than silently no-op.
const TERRAIN_WAIT_MS = 8000;
const TERRAIN_POLL_MS = 100;

/** Fix indices to probe: every N-th fix, both ends included. */
function sampleIndices(data, maxSamples) {
  const n = data.length;
  const step = Math.max(MIN_FIX_STEP, Math.ceil(n / maxSamples));
  const idx = [];
  for (let i = 0; i < n - 1; i += step) idx.push(i);
  idx.push(n - 1);
  return idx;
}

/** The real terrain provider, once it has replaced the default ellipsoid one. */
async function waitForTerrain(viewer) {
  const deadline = performance.now() + TERRAIN_WAIT_MS;
  do {
    const provider = viewer.terrainProvider;
    // EllipsoidTerrainProvider has no availability, and sampling needs it.
    if (provider && provider.availability) return provider;
    await new Promise((resolve) => setTimeout(resolve, TERRAIN_POLL_MS));
  } while (performance.now() < deadline);
  return null;
}

/**
 * Set `dAltitude` on every fix that would otherwise draw inside the terrain.
 * Mutates `data` in place; returns the largest lift applied, in metres.
 */
export async function liftBelowTerrain(viewer, data, maxSamples = MAX_SAMPLES) {
  if (!viewer || !data || data.length < 2) return 0;

  const provider = await waitForTerrain(viewer);
  if (!provider) {
    console.warn("terrainClamp: no terrain provider, track drawn as recorded");
    return 0;
  }

  const idx = sampleIndices(data, maxSamples);
  const probes = idx.map((i) => Cartographic.fromDegrees(data[i].lng, data[i].lat));
  try {
    await sampleTerrainMostDetailed(provider, probes);
  } catch (error) {
    console.warn("terrainClamp: terrain sampling failed", error);
    return 0;
  }

  // How far each probe has to rise to clear the ground.
  const lift = probes.map((probe, s) => {
    const ground = probe.height;
    if (!Number.isFinite(ground)) return 0; // outside terrain coverage
    return Math.min(MAX_LIFT_M, Math.max(0, ground + MARGIN_M - data[idx[s]].altitude));
  });

  // Ramp between probes, so the correction never introduces a step.
  let maxLift = 0;
  for (let s = 0; s < idx.length - 1; s++) {
    const from = idx[s];
    const span = idx[s + 1] - from;
    for (let i = from; i < idx[s + 1]; i++) {
      const l = lift[s] + ((lift[s + 1] - lift[s]) * (i - from)) / span;
      if (l > 0.05) {
        data[i].dAltitude = data[i].altitude + l;
        if (l > maxLift) maxLift = l;
      }
    }
  }
  const last = lift[lift.length - 1];
  if (last > 0.05) {
    data[data.length - 1].dAltitude = data[data.length - 1].altitude + last;
    if (last > maxLift) maxLift = last;
  }

  return maxLift;
}
